import { Injectable, InjectionToken, Type, inject } from '@angular/core';
import { AuthService } from '@app/services/auth.service';
import { FeatureNoticeAudiComponent } from '@app/components/feature-notice/feature-notice-audi.component';
import { ModalOptions, ModalService } from '@app/components/modal/modal.service';

export interface FeatureNotice {
  id: string;
  component: Type<unknown>;
}

/**
 * The notice currently shown to users. To release a new one: replace this whole object
 * with the new id + component
 */
export const CURRENT_FEATURE_NOTICE_TOKEN = new InjectionToken<FeatureNotice | undefined>('CURRENT_FEATURE_NOTICE', {
  providedIn: 'root',
  factory: () => ({ id: 'audi-2026-06', component: FeatureNoticeAudiComponent }),
});

export type FeatureNoticeData = {
  forceShow?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class FeatureNoticeService {
  authService = inject(AuthService);
  modalService = inject(ModalService);
  private notice = inject(CURRENT_FEATURE_NOTICE_TOKEN);

  shouldShowNotice() {
    if (!this.notice) return false;

    const storedValue = localStorage.getItem(this._dontShowAgainKey(this.notice.id));
    return storedValue !== this._storedValue();
  }

  hasShownNoticeOnce() {
    if (!this.notice) return false;

    const shownOnce = localStorage.getItem(this._shownOnceKey(this.notice.id));
    return shownOnce === this._storedValue();
  }

  noticeHasBeenShown() {
    if (!this.notice) return;

    localStorage.setItem(this._shownOnceKey(this.notice.id), this._storedValue());
  }

  dontShowNoticeAgain() {
    if (!this.notice) return;

    localStorage.setItem(this._dontShowAgainKey(this.notice.id), this._storedValue());
  }

  open(props?: FeatureNoticeData, options: ModalOptions = { screen: 'auto' }): string | undefined {
    if (!this.notice) return undefined;

    if (this.shouldShowNotice() && (props?.forceShow || !this.hasShownNoticeOnce())) {
      return this.modalService.open(this.notice.component, props, options);
    }
    return undefined;
  }

  private _dontShowAgainKey(noticeId: string) {
    return `QIRA_NOTICE_${noticeId}_dont_show_again`;
  }

  private _shownOnceKey(noticeId: string) {
    return `QIRA_NOTICE_${noticeId}_is_shown_once`;
  }

  private _storedValue() {
    const user = this.authService.currentUser();
    return `${user?.profile.email}-${user?.codeId ? user.codeId : 'admin'}`;
  }
}
